import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Typography, Button, Space, Divider, Tabs, Select, Tag, Input, message } from 'antd';
import {
    CloseOutlined,
    SaveOutlined,
    EyeOutlined,
    BlockOutlined,
    BuildOutlined,
    AppstoreOutlined,
    SettingOutlined,
    LayoutOutlined,
    ThunderboltOutlined,
    DragOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { useDrag, useDrop } from 'react-dnd';
import TokenSpacingControl from './ui-kit/TokenSpacingControl';
import BoxModelControl from './ui-kit/BoxModelControl';
import AutoLayoutControl from './ui-kit/AutoLayoutControl';
import StateManagerPanel from './StateManagerPanel';
import UIComponentRenderer from './ui-kit/UIComponentRenderer';

const { Text, Title } = Typography;

// Drag type for reordering children
const CHILD_DRAG_TYPE = 'COMPOSITION_CHILD';

/**
 * Draggable/droppable/resizable child component for composition preview
 */
const DraggablePreviewChild = ({
    index,
    child,
    childComponent,
    designKit,
    localConfig,
    onMove,
    onResize,
    direction
}) => {
    const ref = useRef(null);
    const [isOver, setIsOver] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

    // Get current dimensions from child or defaults
    const childWidth = typeof child === 'object' ? (child.width || 'auto') : 'auto';
    const childHeight = typeof child === 'object' ? (child.height || 'auto') : 'auto';

    const [{ isDragging }, drag, preview] = useDrag({
        type: CHILD_DRAG_TYPE,
        item: { index },
        canDrag: !isResizing,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    const [, drop] = useDrop({
        accept: CHILD_DRAG_TYPE,
        hover(item, monitor) {
            if (!ref.current || isResizing) return;
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const isHorizontal = direction === 'row';

            if (isHorizontal) {
                const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
                const clientOffset = monitor.getClientOffset();
                const hoverClientX = clientOffset.x - hoverBoundingRect.left;

                if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) return;
                if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) return;
            } else {
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                const clientOffset = monitor.getClientOffset();
                const hoverClientY = clientOffset.y - hoverBoundingRect.top;

                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
                if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
            }

            onMove(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
        collect: (monitor) => {
            setIsOver(monitor.isOver());
        }
    });

    drag(drop(ref));

    const childId = typeof child === 'string' ? child : child.componentId;
    const childType = typeof child === 'string' ? null : child.componentType;

    // Handle resize start
    const handleResizeStart = (e, corner) => {
        e.stopPropagation();
        e.preventDefault();
        setIsResizing(true);
        const rect = ref.current?.getBoundingClientRect();
        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            width: rect?.width || 100,
            height: rect?.height || 40,
            corner
        });

        const handleMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - e.clientX;
            const deltaY = moveEvent.clientY - e.clientY;

            let newWidth = resizeStart.width;
            let newHeight = resizeStart.height;

            if (corner.includes('right')) {
                newWidth = Math.max(50, (rect?.width || 100) + deltaX);
            }
            if (corner.includes('bottom')) {
                newHeight = Math.max(24, (rect?.height || 40) + deltaY);
            }

            onResize?.(index, { width: Math.round(newWidth), height: Math.round(newHeight) });
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <div
            ref={ref}
            style={{
                flex: localConfig.autoLayout?.align === 'stretch' && childWidth === 'auto' ? 1 : 'none',
                minWidth: 0,
                width: childWidth !== 'auto' ? childWidth : undefined,
                height: childHeight !== 'auto' ? childHeight : undefined,
                opacity: isDragging ? 0.4 : 1,
                cursor: isResizing ? 'nwse-resize' : 'move',
                position: 'relative',
                transition: isResizing ? 'none' : 'all 0.15s ease',
                border: isOver ? '2px dashed #58a6ff' : isResizing ? '2px solid #58a6ff' : '2px solid transparent',
                borderRadius: 8,
                padding: isOver ? 4 : 0
            }}
        >
            {/* Drag handle overlay */}
            <div style={{
                position: 'absolute',
                top: 4,
                right: 4,
                zIndex: 10,
                background: '#21262d',
                borderRadius: 4,
                padding: '2px 6px',
                opacity: 0.8,
                display: 'flex',
                alignItems: 'center',
                gap: 4
            }}>
                <DragOutlined style={{ color: '#8b949e', fontSize: 10 }} />
                {childWidth !== 'auto' && (
                    <Text style={{ color: '#58a6ff', fontSize: 9 }}>{childWidth}×{childHeight}</Text>
                )}
            </div>

            {/* Resize handle - bottom right corner */}
            <div
                onMouseDown={(e) => handleResizeStart(e, 'bottom-right')}
                style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 12,
                    height: 12,
                    cursor: 'nwse-resize',
                    background: 'transparent',
                    zIndex: 15
                }}
            >
                <div style={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    width: 8,
                    height: 8,
                    borderRight: '2px solid #58a6ff',
                    borderBottom: '2px solid #58a6ff',
                    opacity: 0.6
                }} />
            </div>

            {childComponent ? (
                <UIComponentRenderer
                    componentType={childComponent.type}
                    state="default"
                    contentOverrides={{ variant: typeof child === 'string' ? 'default' : (child.variant || 'default') }}
                    deviceType="mobile"
                    designKit={designKit}
                />
            ) : (
                <div style={{
                    padding: '12px 16px',
                    background: '#21262d',
                    border: '1px dashed #30363d',
                    borderRadius: 6
                }}>
                    <Text style={{ color: '#8b949e', fontSize: 11 }}>
                        {childType || childId}
                    </Text>
                </div>
            )}
        </div>
    );
};

/**
 * ComponentEditor - Full-screen Figma-like editor for molecules and organisms
 * Split layout: Preview on left, Editor controls on right
 */
const ComponentEditor = ({
    visible,
    component,
    designKit,
    onClose,
    onSave
}) => {
    // Local state for editing
    const [localConfig, setLocalConfig] = useState({
        padding: {},
        margin: {},
        gap: {},
        children: [],
        autoLayout: {
            direction: 'column',
            align: 'stretch',
            justify: 'start',
            gap: 'space-2'
        },
        states: [{ name: 'default', overrides: {} }]
    });
    const [localVariants, setLocalVariants] = useState([]);
    const [newVariantName, setNewVariantName] = useState('');
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [previewState, setPreviewState] = useState('default');

    // Initialize from component
    useEffect(() => {
        if (component) {
            setLocalConfig({
                padding: component.spacingConfig?.padding || {},
                margin: component.spacingConfig?.margin || {},
                gap: component.spacingConfig?.gap || {},
                children: component.children || [],
                autoLayout: component.autoLayout || {
                    direction: 'column',
                    align: 'stretch',
                    justify: 'start',
                    gap: 'space-2'
                },
                states: component.states || [{ name: 'default', overrides: {} }]
            });
            setLocalVariants(component.variants || []);
            if (component.variants && component.variants.length > 0) {
                setSelectedVariant(component.variants[0]);
            } else {
                setSelectedVariant(null);
            }
            setPreviewState('default');
        }
    }, [component]);

    // Handle moving children (reorder via drag-drop)
    // Must be declared before early return to satisfy hooks rules
    const moveChild = useCallback((dragIndex, hoverIndex) => {
        setLocalConfig(prev => {
            const newChildren = [...prev.children];
            const [draggedItem] = newChildren.splice(dragIndex, 1);
            newChildren.splice(hoverIndex, 0, draggedItem);
            // Update order property
            const updatedChildren = newChildren.map((child, idx) => {
                if (typeof child === 'string') {
                    return { componentId: child, order: idx };
                }
                return { ...child, order: idx };
            });
            return { ...prev, children: updatedChildren };
        });
    }, []);

    // Handle resizing children
    const resizeChild = useCallback((index, dimensions) => {
        setLocalConfig(prev => {
            const newChildren = [...prev.children];
            const child = newChildren[index];
            if (typeof child === 'string') {
                newChildren[index] = { componentId: child, ...dimensions };
            } else {
                newChildren[index] = { ...child, ...dimensions };
            }
            return { ...prev, children: newChildren };
        });
    }, []);

    if (!component) return null;

    const spacingTokens = designKit?.tokens?.spacing || [];
    const isOrganism = component.layer === 'organisms';
    const isMolecule = component.layer === 'molecules';
    const isAtom = component.layer === 'atoms';

    // Get layer styling - muted, professional colors
    const layerColors = {
        atoms: { bg: 'rgba(194, 161, 120, 0.15)', border: 'rgba(194, 161, 120, 0.5)', icon: '#c9a876' },
        molecules: { bg: 'rgba(147, 176, 143, 0.15)', border: 'rgba(147, 176, 143, 0.5)', icon: '#93b08f' },
        organisms: { bg: 'rgba(156, 139, 176, 0.15)', border: 'rgba(156, 139, 176, 0.5)', icon: '#9c8bb0' }
    };
    const layerStyle = layerColors[component.layer] || layerColors.atoms;

    const handleSave = () => {
        onSave?.(component.id, {
            spacingConfig: {
                padding: localConfig.padding,
                margin: localConfig.margin,
                gap: localConfig.gap
            },
            autoLayout: localConfig.autoLayout,
            states: localConfig.states,
            children: localConfig.children,
            variants: localVariants
        });
        message.success('Component updated');
        onClose();
    };

    // Get child component options based on current layer (including variants)
    const getChildOptions = () => {
        if (!designKit?.components) return [];
        const molecules = designKit.components.molecules || [];
        const atoms = designKit.components.atoms || [];
        const options = [];

        const addComponentOptions = (components, layer) => {
            components.forEach(comp => {
                // Add base component (default variant)
                options.push({
                    value: `${comp.id}::default`,
                    componentId: comp.id,
                    label: comp.name,
                    layer,
                    type: comp.type,
                    variant: 'default'
                });
                // Add variant options
                if (comp.variants && comp.variants.length > 0) {
                    comp.variants.forEach(variant => {
                        options.push({
                            value: `${comp.id}::${variant}`,
                            componentId: comp.id,
                            label: `${comp.name} (${variant})`,
                            layer,
                            type: comp.type,
                            variant
                        });
                    });
                }
            });
        };

        if (isMolecule) {
            addComponentOptions(atoms, 'atoms');
        } else if (isOrganism) {
            addComponentOptions(molecules, 'molecules');
            addComponentOptions(atoms, 'atoms');
        }
        return options;
    };

    // Get pixel value from token name
    const getPx = (tokenName) => {
        if (!tokenName || tokenName === '0') return 0;
        if (typeof tokenName === 'number') return tokenName;
        const token = spacingTokens.find(t => t.name === tokenName);
        return token?.values?.mobile || parseInt(tokenName, 10) || 0;
    };

    const paddingTop = getPx(localConfig.padding?.top);
    const paddingRight = getPx(localConfig.padding?.right);
    const paddingBottom = getPx(localConfig.padding?.bottom);
    const paddingLeft = getPx(localConfig.padding?.left);
    const gap = getPx(localConfig.gap?.all);

    const tabItems = [
        {
            key: 'spacing',
            label: 'Spacing',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <BoxModelControl
                        margin={localConfig.margin}
                        padding={localConfig.padding}
                        gap={localConfig.gap}
                        onMarginChange={(v) => setLocalConfig(prev => ({ ...prev, margin: v }))}
                        onPaddingChange={(v) => setLocalConfig(prev => ({ ...prev, padding: v }))}
                        onGapChange={(v) => setLocalConfig(prev => ({ ...prev, gap: v }))}
                        tokens={spacingTokens}
                    />
                </div>
            )
        }
    ];

    // Add variants tab for atoms
    if (isAtom) {
        tabItems.push({
            key: 'variants',
            label: (
                <Space size={4}>
                    <AppstoreOutlined />
                    <span>Variants</span>
                    <span style={{
                        background: '#21262d',
                        color: '#8b949e',
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 10
                    }}>
                        {localVariants.length}
                    </span>
                </Space>
            ),
            children: (
                <div style={{ padding: '16px 0' }}>
                    <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                        Define visual variants for this atom (e.g., primary, secondary, ghost)
                    </Text>

                    {/* List of variants */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                        {localVariants.map((variant, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px',
                                    background: selectedVariant === variant ? 'rgba(88, 166, 255, 0.15)' : '#21262d',
                                    border: selectedVariant === variant ? '1px solid #58a6ff' : '1px solid #30363d',
                                    borderRadius: 6,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSelectedVariant(variant)}
                            >
                                <Text style={{ color: '#e6edf3', fontSize: 13 }}>{variant}</Text>
                                <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<CloseOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLocalVariants(prev => prev.filter(v => v !== variant));
                                        if (selectedVariant === variant) {
                                            setSelectedVariant(localVariants[0] || null);
                                        }
                                    }}
                                />
                            </div>
                        ))}
                        {localVariants.length === 0 && (
                            <div style={{
                                padding: '16px',
                                background: '#21262d',
                                borderRadius: 6,
                                textAlign: 'center'
                            }}>
                                <Text style={{ color: '#484f58', fontSize: 12 }}>
                                    No variants defined. Add variants like "primary", "secondary", "ghost".
                                </Text>
                            </div>
                        )}
                    </div>

                    {/* Add new variant */}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Input
                            placeholder="Enter variant name..."
                            value={newVariantName}
                            onChange={(e) => setNewVariantName(e.target.value)}
                            onPressEnter={() => {
                                if (newVariantName.trim() && !localVariants.includes(newVariantName.trim())) {
                                    setLocalVariants(prev => [...prev, newVariantName.trim()]);
                                    setNewVariantName('');
                                }
                            }}
                            style={{
                                flex: 1,
                                background: '#0d1117',
                                border: '1px solid #30363d',
                                color: '#e6edf3'
                            }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                if (newVariantName.trim() && !localVariants.includes(newVariantName.trim())) {
                                    setLocalVariants(prev => [...prev, newVariantName.trim()]);
                                    setNewVariantName('');
                                }
                            }}
                        >
                            Add
                        </Button>
                    </div>
                </div>
            )
        });
    }

    // Add layout tab for molecules and organisms
    if (isMolecule || isOrganism) {
        tabItems.push({
            key: 'layout',
            label: (
                <Space size={4}>
                    <LayoutOutlined />
                    <span>Layout</span>
                </Space>
            ),
            children: (
                <div style={{ padding: '16px 0' }}>
                    <AutoLayoutControl
                        layout={localConfig.autoLayout}
                        spacingTokens={spacingTokens}
                        onChange={(autoLayout) => setLocalConfig(prev => ({ ...prev, autoLayout }))}
                    />
                </div>
            )
        });
    }

    // Add states tab for molecules and organisms
    if (isMolecule || isOrganism) {
        tabItems.push({
            key: 'states',
            label: (
                <Space size={4}>
                    <ThunderboltOutlined />
                    <span>States</span>
                </Space>
            ),
            children: (
                <div style={{ padding: '16px 0' }}>
                    <StateManagerPanel
                        states={localConfig.states}
                        onChange={(states) => setLocalConfig(prev => ({ ...prev, states }))}
                        previewState={previewState}
                        onPreviewStateChange={setPreviewState}
                    />
                </div>
            )
        });
    }

    // Add composition tab for molecules and organisms
    if (isMolecule || isOrganism) {
        const childTypeLabel = isMolecule ? 'atoms' : 'molecules and atoms';
        const layerIcon = isMolecule ? <AppstoreOutlined style={{ color: '#ffa657' }} /> : <BlockOutlined style={{ color: '#7ee787' }} />;

        tabItems.push({
            key: 'composition',
            label: 'Composition',
            children: (
                <div style={{ padding: '16px 0' }}>
                    <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                        CHILD COMPONENTS
                    </Text>
                    <Text style={{ color: '#484f58', fontSize: 12, display: 'block', marginBottom: 16 }}>
                        Add {childTypeLabel} that make up this {isMolecule ? 'molecule' : 'organism'}
                    </Text>

                    {/* Current children */}
                    <div style={{ marginBottom: 16 }}>
                        {localConfig.children.length === 0 ? (
                            <div style={{
                                padding: 24,
                                border: '2px dashed #30363d',
                                borderRadius: 8,
                                textAlign: 'center'
                            }}>
                                <Text style={{ color: '#484f58', fontSize: 12 }}>
                                    No children yet. Add {childTypeLabel} below.
                                </Text>
                            </div>
                        ) : (
                            localConfig.children.map((child, idx) => {
                                // Support both string IDs and object format
                                const childId = typeof child === 'string' ? child : child.componentId;
                                const childVariant = typeof child === 'string' ? 'default' : (child.variant || 'default');
                                const childInfo = getChildOptions().find(c => c.componentId === childId && c.variant === childVariant) ||
                                    getChildOptions().find(c => c.componentId === childId);
                                return (
                                    <div key={idx} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '10px 12px',
                                        background: '#161b22',
                                        border: '1px solid #30363d',
                                        borderRadius: 6,
                                        marginBottom: 8
                                    }}>
                                        <span style={{ color: childInfo?.layer === 'molecules' ? '#7ee787' : '#ffa657' }}>
                                            {childInfo?.layer === 'molecules' ? <BlockOutlined /> : <AppstoreOutlined />}
                                        </span>
                                        <div style={{ flex: 1 }}>
                                            <Text style={{ color: '#e6edf3', fontSize: 13, display: 'block' }}>
                                                {childInfo?.label?.split(' (')[0] || childId}
                                            </Text>
                                            {childVariant && childVariant !== 'default' && (
                                                <Text style={{ color: '#58a6ff', fontSize: 11 }}>
                                                    {childVariant}
                                                </Text>
                                            )}
                                        </div>
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<CloseOutlined />}
                                            onClick={() => {
                                                setLocalConfig(prev => ({
                                                    ...prev,
                                                    children: prev.children.filter((_, i) => i !== idx)
                                                }));
                                            }}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Add child selector */}
                    <Select
                        placeholder={`Add ${isMolecule ? 'atom' : 'component'}...`}
                        options={getChildOptions().map(opt => ({
                            ...opt,
                            searchLabel: opt.label,
                            label: (
                                <Space>
                                    {opt.layer === 'molecules' ? <BlockOutlined style={{ color: '#7ee787' }} /> : <AppstoreOutlined style={{ color: '#ffa657' }} />}
                                    {opt.label}
                                </Space>
                            )
                        }))}
                        style={{ width: '100%' }}
                        onSelect={(value) => {
                            const selectedComp = getChildOptions().find(c => c.value === value);
                            setLocalConfig(prev => ({
                                ...prev,
                                children: [...prev.children, {
                                    componentId: selectedComp?.componentId,
                                    componentType: selectedComp?.type,
                                    variant: selectedComp?.variant || 'default',
                                    order: prev.children.length,
                                    spacing: { top: 'space-0', bottom: 'space-0' }
                                }]
                            }));
                        }}
                        value={null}
                        showSearch
                        optionFilterProp="searchLabel"
                        virtual={false}
                    />
                </div>
            )
        });
    }

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            width="90vw"
            style={{ top: 20, maxWidth: 1400 }}
            styles={{
                body: { padding: 0, background: '#0d1117' },
                content: { background: '#0d1117', borderRadius: 12 }
            }}
        >
            {/* Main Container */}
            <div style={{ display: 'flex', height: '80vh', maxHeight: 800 }}>

                {/* LEFT SIDE - Preview */}
                <div style={{
                    flex: 1,
                    background: '#161b22',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '1px solid #30363d'
                }}>
                    {/* Preview Header */}
                    <div style={{
                        padding: '16px 24px',
                        borderBottom: '1px solid #30363d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <EyeOutlined style={{ color: '#58a6ff', fontSize: 16 }} />
                            <Text style={{ color: '#e6edf3', fontWeight: 600 }}>Preview</Text>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {component?.variants?.length > 0 && (
                                <Select
                                    value={selectedVariant}
                                    onChange={setSelectedVariant}
                                    size="small"
                                    options={component.variants.map(v => ({ label: v, value: v }))}
                                    style={{ width: 120 }}
                                />
                            )}
                            <Tag color="green" style={{ fontSize: 10 }}>
                                P: {paddingTop}/{paddingRight}/{paddingBottom}/{paddingLeft}
                            </Tag>
                            {gap > 0 && (
                                <Tag color="blue" style={{ fontSize: 10 }}>
                                    Gap: {gap}px
                                </Tag>
                            )}
                        </div>
                    </div>

                    {/* Preview Canvas */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 40,
                        background: `
                            linear-gradient(90deg, #21262d 1px, transparent 1px),
                            linear-gradient(#21262d 1px, transparent 1px)
                        `,
                        backgroundSize: '20px 20px'
                    }}>
                        {/* Isolated Component Preview */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                            {/* Margin Layer (outer) */}
                            <div style={{
                                width: '70%',
                                minWidth: 375,
                                padding: `${getPx(localConfig.margin?.top)}px ${getPx(localConfig.margin?.right)}px ${getPx(localConfig.margin?.bottom)}px ${getPx(localConfig.margin?.left)}px`,
                                background: 'rgba(255, 183, 128, 0.1)',
                                border: '1px dashed rgba(255, 183, 128, 0.4)',
                                borderRadius: 12,
                                position: 'relative'
                            }}>
                                {/* Margin indicators */}
                                {getPx(localConfig.margin?.top) > 0 && (
                                    <div style={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: 'rgba(255, 183, 128, 0.8)' }}>
                                        {getPx(localConfig.margin?.top)}
                                    </div>
                                )}
                                {getPx(localConfig.margin?.bottom) > 0 && (
                                    <div style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: 'rgba(255, 183, 128, 0.8)' }}>
                                        {getPx(localConfig.margin?.bottom)}
                                    </div>
                                )}
                                {getPx(localConfig.margin?.left) > 0 && (
                                    <div style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'rgba(255, 183, 128, 0.8)' }}>
                                        {getPx(localConfig.margin?.left)}
                                    </div>
                                )}
                                {getPx(localConfig.margin?.right) > 0 && (
                                    <div style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'rgba(255, 183, 128, 0.8)' }}>
                                        {getPx(localConfig.margin?.right)}
                                    </div>
                                )}

                                {/* Padding Layer (inner) */}
                                <div style={{
                                    padding: `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`,
                                    background: 'rgba(130, 200, 130, 0.1)',
                                    border: '1px dashed rgba(130, 200, 130, 0.4)',
                                    borderRadius: 8,
                                    position: 'relative',
                                    width: '100%',
                                    overflow: 'hidden',
                                    boxSizing: 'border-box'
                                }}>
                                    {/* Padding indicators */}
                                    {paddingTop > 0 && (
                                        <div style={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: 'rgba(130, 200, 130, 0.8)' }}>
                                            {paddingTop}
                                        </div>
                                    )}
                                    {paddingBottom > 0 && (
                                        <div style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: 'rgba(130, 200, 130, 0.8)' }}>
                                            {paddingBottom}
                                        </div>
                                    )}
                                    {paddingLeft > 0 && (
                                        <div style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'rgba(130, 200, 130, 0.8)' }}>
                                            {paddingLeft}
                                        </div>
                                    )}
                                    {paddingRight > 0 && (
                                        <div style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'rgba(130, 200, 130, 0.8)' }}>
                                            {paddingRight}
                                        </div>
                                    )}

                                    {/* Component container with auto-layout for children */}
                                    <div style={{
                                        width: '100%',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: localConfig.autoLayout?.direction === 'row' ? 'row' : 'column',
                                        alignItems: localConfig.autoLayout?.align === 'start' ? 'flex-start' :
                                            localConfig.autoLayout?.align === 'end' ? 'flex-end' :
                                                localConfig.autoLayout?.align === 'center' ? 'center' : 'stretch',
                                        justifyContent: localConfig.autoLayout?.justify === 'start' ? 'flex-start' :
                                            localConfig.autoLayout?.justify === 'end' ? 'flex-end' :
                                                localConfig.autoLayout?.justify === 'center' ? 'center' :
                                                    localConfig.autoLayout?.justify === 'space-between' ? 'space-between' :
                                                        localConfig.autoLayout?.justify === 'space-around' ? 'space-around' : 'flex-start',
                                        gap: getPx(localConfig.autoLayout?.gap) || gap
                                    }}>
                                        {localConfig.children.length > 0 ? (
                                            localConfig.children.map((child, idx) => {
                                                const childId = typeof child === 'string' ? child : child.componentId;
                                                const childType = typeof child === 'string' ? null : child.componentType;
                                                const childComponent = designKit?.components?.atoms?.find(a => a.id === childId || a.type === childType) ||
                                                    designKit?.components?.molecules?.find(m => m.id === childId || m.type === childType);

                                                return (
                                                    <DraggablePreviewChild
                                                        key={childId + idx}
                                                        index={idx}
                                                        child={child}
                                                        childComponent={childComponent}
                                                        designKit={designKit}
                                                        localConfig={localConfig}
                                                        onMove={moveChild}
                                                        onResize={resizeChild}
                                                        direction={localConfig.autoLayout?.direction || 'column'}
                                                    />
                                                );
                                            })
                                        ) : (
                                            <UIComponentRenderer
                                                componentType={component.type}
                                                state="default"
                                                contentOverrides={{ variant: selectedVariant }}
                                                deviceType="mobile"
                                                designKit={designKit}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT SIDE - Editor */}
                <div style={{
                    width: 380,
                    background: '#0d1117',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Editor Header */}
                    <div style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #30363d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ color: layerStyle.icon }}>
                                    {isOrganism ? <BuildOutlined /> : <BlockOutlined />}
                                </span>
                                <Title level={5} style={{ margin: 0, color: '#e6edf3' }}>
                                    {component.name}
                                </Title>
                            </div>
                            <Tag style={{
                                background: layerStyle.bg,
                                border: `1px solid ${layerStyle.border}`,
                                color: layerStyle.icon
                            }}>
                                {component.layer}
                            </Tag>
                        </div>
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={onClose}
                            style={{ color: '#8b949e' }}
                        />
                    </div>

                    {/* Editor Content */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0 20px'
                    }}>
                        <Tabs
                            items={tabItems}
                            size="small"
                        />
                    </div>

                    {/* Footer Actions */}
                    <div style={{
                        padding: '16px 20px',
                        borderTop: '1px solid #30363d',
                        display: 'flex',
                        gap: 12
                    }}>
                        <Button onClick={onClose} style={{ flex: 1 }}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            style={{ flex: 1 }}
                        >
                            Save Changes
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ComponentEditor;
